import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  BookOpen,
  FolderOpen,
  Users,
  BarChart3,
  Settings,
  Bell,
  Shield,
  GraduationCap,
  Upload,
  FileText,
  CreditCard,
  UserCheck,
  Target,
  X,
} from 'lucide-react';

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
  },
  {
    name: 'Cursos',
    href: '/courses',
    icon: BookOpen,
  },
  {
    name: 'Categorías',
    href: '/categories',
    icon: FolderOpen,
  },
  {
    name: 'Ejercicios',
    href: '/exercises',
    icon: Target,
  },
  {
    name: 'Pagos',
    href: '/payments',
    icon: CreditCard,
  },
  {
    name: 'Estudiantes',
    href: '/students',
    icon: Users,
  },
  {
    name: 'Instructores',
    href: '/instructors',
    icon: UserCheck,
  },
  {
    name: 'Subir Contenido',
    href: '/upload',
    icon: Upload,
  },
  {
    name: 'Galería',
    href: '/gallery',
    icon: FolderOpen,
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
  },
  {
    name: 'Reportes',
    href: '/reports',
    icon: FileText,
  },
  {
    name: 'Notificaciones',
    href: '/notifications',
    icon: Bell,
  },
  {
    name: 'Seguridad',
    href: '/security',
    icon: Shield,
  },
  {
    name: 'Configuración',
    href: '/settings',
    icon: Settings,
  },
];

interface SidebarProps {
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, onMobileClose }) => {
  const location = useLocation();

  return (
    <>
      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 z-50 ${isMobileOpen ? 'block' : 'hidden'} md:hidden`}>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onMobileClose}
        />

        {/* Sidebar */}
        <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border shadow-lg">
          <div className="flex flex-col h-full">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center">
                <div className="bg-primary p-2 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-primary-foreground" />
                </div>
                <div className="ml-3">
                  <h1 className="text-lg font-semibold text-foreground">
                    EduAdmin
                  </h1>
                  <p className="text-xs text-muted-foreground">Agentesia</p>
                </div>
              </div>
              <button
                onClick={onMobileClose}
                className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href;

                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={onMobileClose}
                    className={clsx(
                      'group flex items-center px-2 py-3 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <item.icon
                      className={clsx(
                        'mr-3 flex-shrink-0 h-5 w-5',
                        isActive
                          ? 'text-primary'
                          : 'text-muted-foreground group-hover:text-accent-foreground'
                      )}
                    />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto bg-card border-r border-border">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-4">
            <div className="flex items-center">
              <div className="bg-primary p-2 rounded-lg">
                <GraduationCap className="h-6 w-6 text-primary-foreground" />
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-foreground">
                  EduAdmin
                </h1>
                <p className="text-xs text-muted-foreground">Agentesia</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="mt-8 flex-1 px-2 space-y-1">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.href;

              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon
                    className={clsx(
                      'mr-3 flex-shrink-0 h-5 w-5',
                      isActive
                        ? 'text-primary'
                        : 'text-muted-foreground group-hover:text-accent-foreground'
                    )}
                  />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};